<template>
  <div class="attachments-list card">
    <spinner :show="loading" v-if="loading"/>
    <template v-else>
      <div class="card-header d-flex justify-content-between" v-if="!hideHeader">
        <h5 class="m-0">{{ $t('Attachments') }}</h5>
      </div>
      <div class="card-body">
        <file-uploader v-if="$hasPermission(changePermission)" :show-preview="false" @onItemsAdd="onItemsAdd"
                       :items-prop="files" :hint-text="false"
                       :uploading-progress="uploadingProgress" :uploading-progress-value="uploadingProgressValue"
                       :accept="fileUploaderAccept" ref="fileUploader"/>
        <artisio-table v-if="attachments && attachments.length > 0" :items="attachments" :fields="tableFields"
                       :index="false">
          <template slot="cell(name)" slot-scope="{item: file}">
            <div class="d-flex justify-content-start align-items-center">
              <div style="width: 3em" class="text-center">
                <template v-if="file.content_type.includes('image/')">
                  <b-img fluid :src="file.url"/>
                </template>
                <template v-else>
                  <font-awesome-icon :icon="getIcon(file.content_type)" style="font-size: 2em;"
                                     :class="{'text-muted': file.content_type !== 'application/pdf', 'text-danger': file.content_type === 'application/pdf' }"/>
                </template>
              </div>
              <div class="ml-4">
                <b-button variant="link" class="p-0 text-left" @click="onPreviewClick(file)">{{ file.name }}</b-button>
              </div>
            </div>
          </template>
          <template slot="cell(created_at)" slot-scope="data">
            {{ data.item.created_at | toDateTime }}
          </template>
          <template slot="cell(created_by)" slot-scope="data">
            {{ data.item.created_by ? data.item.created_by.display_name : '' }}
          </template>
          <template slot="cell(actions)" slot-scope="{item: file}">
            <div style="min-height: 2em" class="d-flex align-items-center justify-content-center">
              <b-tooltip :target="`copy-url-${file.uuid}`" :title="attachmentCopyTooltip[file.uuid] || $t('Copy the attachment URL')"/>
              <b-button @click="copyAttachmentUrl(file)" :id="`copy-url-${file.uuid}`" variant="primary" size="sm" class="mr-2">
                <font-awesome-icon icon="copy" class="icon"/>
              </b-button>

              <b-tooltip :target="`file-delete-${file.uuid}`" :title="$t('Delete')"/>
              <b-button v-permission="changePermission" :id="`file-delete-${file.uuid}`" variant="danger" size="sm" @click="onFileRemoveClick(file)">
                <font-awesome-icon icon="times" class="icon"/>
              </b-button>
            </div>
          </template>
        </artisio-table>
        <no-data-available v-else-if="!$hasPermission(changePermission)" height="10"/>
      </div>
    </template>
    <attachment-view-modal/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from 'vuex';
  import Spinner from "../../core/components/common/Spinner";
  import FileUploader from "@/core/components/file-uploader/FileUploader";
  import ArtisioTable from "@/core/components/ams-table/ArtisioTable";
  import i18n from "@/shared/i18n";
  import AttachmentViewModal from "@/modules/attachments/AttachmentViewModal";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable";

  const {mapState, mapActions} = createNamespacedHelpers('attachments');
  const {mapState: globalMapSate} = createNamespacedHelpers('global');

  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: "AttachmentList",
    components: {NoDataAvailable, AttachmentViewModal, ArtisioTable, FileUploader, Spinner},
    props: {
      objectUuid: {
        type: String,
        required: true
      },
      objectName: {
        type: String,
        required: true
      },
      pluralObjectName: {
        type: String,
        required: true
      },
      changePermission: {
        type: String,
        default: null
      },
      deletePermission: {
        type: String,
        default: null
      },
      truncate: {
        type: [Number, Boolean],
        default: false
      },
      attachmentType: {
        type: String,
        default: null
      },
      hideHeader: {
        type: Boolean,
        default: false
      },
      acceptOnlyImages: {
        type: Boolean,
        default: false
      },
      excludeImages: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        uploadingProgressValue: 0,
        uploadingProgress: false,
        files: [],
        tableFields: [
          {key: "name", label: i18n.t('File Name'), sortable: false},
          {key: "content_type", label: i18n.t('File Type'), sortable: false},
          {key: "created_by", label: i18n.t('Created by'), sortable: false},
          {key: "created_at", label: i18n.t('Creation Date'), sortable: false},
          {key: "actions", label: '', sortable: false},
        ],
        attachmentCopyTooltip: {
          // uuid: 'tooltip'
        }
      }
    },
    computed: {
      ...globalMapSate([
        'attachmentFileTypes',
        'customerMaxAttachmentCount',
        'customerMaxAttachmentSizeSum',
        'inventoryMaxAttachmentCount',
        'inventoryMaxAttachmentSizeSum',
        'attachmentMaxFileSize',
      ]),
      ...mapState({
        attachments: state => state.tableData.rows,
        loading: state => state.loading,
        object_name: state => state.object_name,
        object_uuid: state => state.object_uuid,
      }),
      fileUploaderAccept() {
        let formats = this.attachmentFileTypes;

        if (this.acceptOnlyImages) {
          formats = formats.filter(type => type.startsWith('image/'));
        } else if (this.excludeImages) {
          formats = formats.filter(type => !type.startsWith('image/'));
        }

        return [{formats, size: this.attachmentMaxFileSize}];
      }
    },
    methods: {
      ...mapActions(['getAttachments', 'setData', 'createAttachments', 'deleteAttachment', 'showViewModal']),
      async onItemsAdd() {
        const maxCount = this[`${this.object_name}MaxAttachmentCount`];
        const maxSize = this[`${this.object_name}MaxAttachmentSizeSum`];
        const filesSumSize = this.files.reduce((a, b) => a + b.file.size, 0) / 1000000; // Convert bytes to megabytes
        const attachmentsSumSize = this.attachments.reduce((a, b) => a + b.size, 0) / 1000000; // Convert bytes to megabytes

        if (this.attachments.length + this.files.length > maxCount) {
          this.clearData();
          this.$errorDialog(
            this.$i18n.t("You can not upload more than {count} files.", {count: maxCount}),
            this.$i18n.t('Attachments')
          );
          return;
        }

        if (filesSumSize + attachmentsSumSize > maxSize) {
          this.clearData();
          this.$errorDialog(
            this.$i18n.t("Sum max size limit exceeded. Max size allowed {size} mbs.", {size: maxSize}),
            this.$i18n.t('Attachments')
          );
          return;
        }

        let formData = new FormData();
        this.files.forEach(item => formData.append('file', item.file))
        formData.append(`${this.object_name}_uuid`, this.object_uuid);
        if (this.attachmentType) {
          formData.append('type', this.attachmentType);
        }

        this.uploadingProgress = true;
        let response = await this.createAttachments({
          formData, onUploadProgress: (progressEvent) => {
            this.uploadingProgressValue = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
          }
        });
        if (response.success) {
          this.clearData();
          this.$toast('success', this.$i18n.t('Files were uploaded successfully'));
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.body.length) {
            const listItemsArr = [];
            response.body.forEach(err => {
              if (err.file) {
                err.file.map(e => (listItemsArr.push(this.$createElement('li', {}, e))))
              }
            })
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr));
          }
          this.clearData();
        }
        this.uploadingProgress = false;
        this.uploadingProgressValue = 0;
      },
      onFileRemoveClick(file) {
        this.$confirm(
          this.$i18n.t("Are you sure you want to delete following attachment?"),
          this.$i18n.t('Attachment was successfully deleted'),
          this.deleteAttachment,
          file.uuid
        );
      },
      getIcon(contentType) {
        if (contentType.includes('video/')) {
          return 'file-video';
        } else if (contentType.includes('audio/')) {
          return 'file-audio'
        } else if (contentType.includes('application/pdf')) {
          return 'file-pdf'
        }
        return 'file';
      },
      onPreviewClick(file) {
        let noPreviewTypes = [
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/msword"
        ];
        if (noPreviewTypes.includes(file.content_type)) {
          window.open(file.url);
        } else {
          this.showViewModal(file);
        }
      },
      clearData() {
        this.files = [];
        this.$refs.fileUploader.clearInput();
      },
      copyAttachmentUrl(file) {
        // Replace 'your-text-to-copy' with the actual text you want to copy
        const textToCopy = file.url

        // Create a temporary element to copy the text
        const tempInput = document.createElement('input');
        tempInput.value = textToCopy;
        document.body.appendChild(tempInput);

        // Select the text in the temporary input
        tempInput.select();
        document.execCommand('copy');

        // Remove the temporary input from the DOM
        document.body.removeChild(tempInput);

        this.attachmentCopyTooltip = {
          ...this.attachmentCopyTooltip,
          [file.uuid]: this.$t('Copied!')
        }
        setTimeout(() => {
          delete this.attachmentCopyTooltip[file.uuid]
          this.attachmentCopyTooltip = {...this.attachmentCopyTooltip}
        }, 3000)
      }
    },
    mounted() {
      this.getAttachments();
    },
    beforeMount() {
      this.setData({
        object_uuid: this.objectUuid,
        object_name: this.objectName,
        plural_object_name: this.pluralObjectName,
        attachment_type: this.attachmentType,
      })
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  /deep/ .artisio-table {
    td {
      vertical-align: middle;
    }
  }
</style>
